import React, {
  useState, useEffect, useLayoutEffect, useCallback, useRef,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import useProfileCompletion from '../../hooks/shared/useProfileCompletion';
import ProfileCompletionModal from './ProfileCompletionModal';
import {
  getToken,
  isTokenValid,
  isTokenExpired,
  isTokenExpiringSoon,
  refreshToken,
  isSessionTimedOut,
  updateLastActivity,
} from '../../utils/auth/tokenManager';
import { safeInternalPath } from '../../utils/auth/guestBrowseIntent';
import {
  logout,
  selectPermissions,
} from '../../redux/features/authSlice';
import { getApiUrl } from '../../redux/api';
import {
  fetchWizardRequiredComplete,
  getWizardOnboardingPath,
  isAllowedWithoutWizard,
  isWizardOnboardingPath,
  readRecentWizardCheck,
  readSettledWizardCheck,
  resetRecentWizardCheck,
  retryWizardRequiredComplete,
} from '../../utils/shared/onboardingTaskNavigation';

// Routes that should be accessible even with incomplete profiles
const ALLOWED_INCOMPLETE_ROUTES = [
  '/influencer-onboarding',
  '/brand-onboarding',
  '/onboarding',
  '/login',
  '/logout',
  '/sign-up',
  '/signup',
  '/apply/campaign',
];

// Token validation interval (5 minutes)
const TOKEN_VALIDATION_INTERVAL = 5 * 60 * 1000;
const SOFT_NAV_VALIDATION_TTL_MS = 30000;

let recentTokenValidation = {
  token: null,
  tokenState: null,
  validatedAt: 0,
};

/** Clears soft-nav validation cache (for tests). */
export function resetRecentTokenValidation() {
  recentTokenValidation = {
    token: null,
    tokenState: null,
    validatedAt: 0,
  };
  resetRecentWizardCheck();
}

/**
 * Loading component for authentication checks
 */
function AuthLoadingState({ message = 'Verifying authentication...' }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

/**
 * Error state component for auth failures
 */
function AuthErrorState({ message, onRetry, onLogin }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        p: 3,
      }}
    >
      <Alert severity="error" sx={{ maxWidth: 400 }}>
        {message}
      </Alert>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {onRetry && (
          <Button variant="outlined" onClick={onRetry}>
            Retry
          </Button>
        )}
        {onLogin && (
          <Button variant="contained" onClick={onLogin}>
            Go to Login
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Permission denied component
 */
function PermissionDenied() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        p: 3,
      }}
    >
      <Alert severity="error" sx={{ maxWidth: 400 }}>
        You do not have permission to access this page.
      </Alert>
    </Box>
  );
}

/**
 * Custom hook for token validation
 */
function readRecentTokenValidation() {
  const now = Date.now();
  if (
    !recentTokenValidation.tokenState
    || now - recentTokenValidation.validatedAt >= SOFT_NAV_VALIDATION_TTL_MS
  ) {
    return null;
  }
  return recentTokenValidation.tokenState;
}

function useTokenValidation(skip = false) {
  const recentTokenState = skip ? null : readRecentTokenValidation();
  const [isValidating, setIsValidating] = useState(!skip && !recentTokenState);
  const [tokenState, setTokenState] = useState(() => recentTokenState || {
    isValid: false,
    isExpired: true,
    needsRefresh: false,
  });
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const hasValidatedRef = useRef(Boolean(recentTokenState));

  const validateToken = useCallback(async ({ showLoader } = {}) => {
    if (skip) {
      setIsValidating(false);
      setTokenState({ isValid: true, isExpired: false, needsRefresh: false });
      return;
    }

    try {
      const token = getToken();
      const now = Date.now();
      const canUseRecentValidation = token
        && recentTokenValidation.token === token
        && recentTokenValidation.tokenState
        && now - recentTokenValidation.validatedAt < SOFT_NAV_VALIDATION_TTL_MS;

      if (canUseRecentValidation) {
        setTokenState(recentTokenValidation.tokenState);
        setError(null);
        setIsValidating(false);
        hasValidatedRef.current = true;
        updateLastActivity();
        return;
      }

      const shouldShowLoader = showLoader ?? !hasValidatedRef.current;
      if (shouldShowLoader) {
        setIsValidating(true);
      }
      setError(null);

      // No token - not authenticated
      if (!token) {
        setTokenState({ isValid: false, isExpired: true, needsRefresh: false });
        setIsValidating(false);
        return;
      }

      if (isTokenExpired(token)) {
        // Try to refresh
        const newToken = await refreshToken(getApiUrl());
        if (newToken) {
          setTokenState({ isValid: true, isExpired: false, needsRefresh: false });
        } else {
          setTokenState({ isValid: false, isExpired: true, needsRefresh: false });
          dispatch(logout());
        }
        setIsValidating(false);
        return;
      }

      // Token is valid, check if it needs refresh soon
      const needsRefresh = isTokenExpiringSoon(token);
      if (needsRefresh) {
        // Background refresh
        refreshToken(getApiUrl()).catch((err) => {
          console.error('Background token refresh failed:', err);
        });
      }

      updateLastActivity();

      const nextTokenState = { isValid: true, isExpired: false, needsRefresh };
      recentTokenValidation = {
        token,
        tokenState: nextTokenState,
        validatedAt: Date.now(),
      };
      setTokenState(nextTokenState);
    } catch (err) {
      console.error('Token validation error:', err);
      setError('Authentication verification failed');
      // Fail-secure: treat as invalid
      setTokenState({ isValid: false, isExpired: true, needsRefresh: false });
    } finally {
      hasValidatedRef.current = true;
      setIsValidating(false);
    }
  }, [skip, dispatch]);

  useEffect(() => {
    validateToken({ showLoader: !hasValidatedRef.current });
  }, [validateToken, location.pathname]);

  useEffect(() => {
    if (skip) return undefined;

    const intervalId = setInterval(() => {
      validateToken({ showLoader: false });
    }, TOKEN_VALIDATION_INTERVAL);
    return () => clearInterval(intervalId);
  }, [validateToken, skip]);

  return {
    isValidating,
    tokenState,
    error,
    revalidate: validateToken,
  };
}

/**
 * Check if user has required permissions
 * @param {string[]} userPermissions - User's permissions
 * @param {string|string[]} required - Required permission(s)
 * @param {string} mode - 'all' requires all permissions, 'any' requires any one
 * @returns {boolean} True if permission check passes
 */
function isBrandOrInfluencer(userType) {
  return userType === 'brand' || userType === 'influencer';
}

function hasValidAuthToken() {
  try {
    return isTokenValid(getToken());
  } catch {
    return false;
  }
}

function storedWizardAllowsAccess(settledCheck) {
  return settledCheck?.complete === true;
}

function initialWizardComplete(skipCheck, settledCheck) {
  if (skipCheck) return true;
  return storedWizardAllowsAccess(settledCheck);
}

function wizardGateFromPath(location) {
  return safeInternalPath(`${location.pathname}${location.search}`);
}

function renderConfirmedWizardReturn(location, confirmedComplete) {
  if (confirmedComplete !== true || !isWizardOnboardingPath(location.pathname)) return null;
  const gateFrom = safeInternalPath(location.state?.wizardGateFrom);
  const currentPath = wizardGateFromPath(location);
  if (!gateFrom || gateFrom === currentPath) return null;
  return <Navigate to={gateFrom} replace />;
}

function renderWizardOnboardingRedirect(userType, location) {
  const onboardingPath = getWizardOnboardingPath(userType);
  if (!onboardingPath) return null;
  return (
    <Navigate
      to={onboardingPath}
      replace
      state={{ wizardGateFrom: wizardGateFromPath(location) }}
    />
  );
}

function useWizardRequiredGate(userType, skip) {
  const skipCheck = skip
    || !isBrandOrInfluencer(userType)
    || !hasValidAuthToken();
  const settledCheck = skipCheck ? null : readSettledWizardCheck(userType);
  const allowsAccess = storedWizardAllowsAccess(settledCheck);
  const recentDenial = !skipCheck && readRecentWizardCheck(userType)?.complete === false;
  const [isChecking, setIsChecking] = useState(
    !skipCheck && !allowsAccess && !recentDenial,
  );
  const [isComplete, setIsComplete] = useState(
    () => initialWizardComplete(skipCheck, settledCheck),
  );
  const [error, setError] = useState(null);
  const [confirmedComplete, setConfirmedComplete] = useState(null);
  const hasCheckedRef = useRef(allowsAccess || recentDenial);
  const requestIdRef = useRef(0);
  const skipCheckRef = useRef(skipCheck);
  skipCheckRef.current = skipCheck;

  const gateEntryKey = skipCheck ? 'skip' : `check:${userType}`;
  const [gateEntryKeyState, setGateEntryKeyState] = useState(gateEntryKey);
  if (gateEntryKeyState !== gateEntryKey) {
    setGateEntryKeyState(gateEntryKey);
    if (!skipCheck && !allowsAccess && !recentDenial) {
      hasCheckedRef.current = false;
      if (!isChecking) setIsChecking(true);
      if (error) setError(null);
    }
  }

  const beginRequest = useCallback(() => {
    requestIdRef.current += 1;
    return requestIdRef.current;
  }, []);

  const applyWizardResult = useCallback((promise, requestId) => {
    promise
      .then((complete) => {
        if (requestIdRef.current !== requestId) return;
        hasCheckedRef.current = true;
        setConfirmedComplete(complete);
        setIsComplete(complete);
        setError(null);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        if (readSettledWizardCheck(userType)?.complete === true) return;
        hasCheckedRef.current = false;
        setConfirmedComplete(false);
        setIsComplete(false);
        setError('Unable to verify onboarding status.');
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setIsChecking(false);
      });
  }, [userType]);

  const retry = useCallback(() => {
    if (skipCheckRef.current) return;
    hasCheckedRef.current = false;
    setError(null);
    setIsChecking(true);
    const requestId = beginRequest();
    applyWizardResult(retryWizardRequiredComplete(userType), requestId);
  }, [applyWizardResult, beginRequest, userType]);

  useLayoutEffect(() => {
    if (skipCheck) {
      setIsChecking(false);
      setError(null);
      return undefined;
    }

    if (readRecentWizardCheck(userType)?.complete === false) {
      hasCheckedRef.current = true;
      setConfirmedComplete(false);
      setIsComplete(false);
      setIsChecking(false);
      setError(null);
      return undefined;
    }

    const settled = readSettledWizardCheck(userType);
    if (storedWizardAllowsAccess(settled)) {
      setIsComplete(true);
      setError(null);
      setIsChecking(false);
      hasCheckedRef.current = true;
    } else if (!hasCheckedRef.current) {
      setIsChecking(true);
      setError(null);
    }

    const requestId = beginRequest();
    applyWizardResult(fetchWizardRequiredComplete(userType), requestId);

    return () => {
      // The onboarding redirect flips skipCheck. Keep the profile request
      // so a complete result can still move the user back off the wizard.
      if (skipCheckRef.current) return;
      requestIdRef.current += 1;
    };
  }, [applyWizardResult, beginRequest, skipCheck, userType]);

  useEffect(() => {
    if (skipCheck) return undefined;

    const retryUnresolvedCheck = () => {
      if (document.visibilityState !== 'visible') return;
      if (hasCheckedRef.current) return;
      retry();
    };

    document.addEventListener('visibilitychange', retryUnresolvedCheck);
    return () => document.removeEventListener('visibilitychange', retryUnresolvedCheck);
  }, [retry, skipCheck]);

  const visibleError = skipCheck || allowsAccess ? null : error;
  const unresolved = !skipCheck && !allowsAccess && !recentDenial && !hasCheckedRef.current;
  const visibleChecking = !visibleError && !recentDenial && (
    unresolved || (!skipCheck && !allowsAccess && isChecking)
  );
  const visibleComplete = skipCheck || (
    !recentDenial && (allowsAccess || (hasCheckedRef.current && isComplete))
  );

  return {
    isChecking: visibleChecking,
    isComplete: visibleComplete,
    error: visibleError,
    retry,
    confirmedComplete,
  };
}

function checkPermissions(userPermissions, required, mode = 'all') {
  if (!required || (Array.isArray(required) && required.length === 0)) {
    return true;
  }

  const requiredArray = Array.isArray(required) ? required : [required];
  const permissions = userPermissions || [];

  if (mode === 'any') {
    return requiredArray.some((p) => permissions.includes(p));
  }

  return requiredArray.every((p) => permissions.includes(p));
}

function ProtectedRoute({
  children,
  requiredUserType,
  requireCompleteProfile = true,
  requiredPermissions = [],
  permissionMode = 'all', // 'all' or 'any'
  failSecure = true, // Block access on errors instead of allowing
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userType = useSelector((state) => state.auth.userType);
  const permissions = useSelector(selectPermissions);

  const skipProfileCheck = !requireCompleteProfile
    || ALLOWED_INCOMPLETE_ROUTES.some((route) => location.pathname.startsWith(route))
    || location.pathname.startsWith('/dashboard')
    || location.pathname.startsWith('/marketplace')
    || location.pathname.startsWith('/admin');
  const skipWizardGate = userType === 'admin' || isAllowedWithoutWizard(location.pathname);

  // Token validation with fail-secure behavior
  const {
    isValidating: isTokenValidating,
    tokenState,
    error: tokenError,
    revalidate,
  } = useTokenValidation(false);

  // Profile completion hook
  const {
    isComplete,
    isLoading: isProfileLoading,
    missingFields,
    completionPercentage,
    error: profileError,
  } = useProfileCompletion(userType, skipProfileCheck);

  const {
    isChecking: isWizardChecking,
    isComplete: isWizardComplete,
    error: wizardError,
    retry: retryWizardCheck,
    confirmedComplete,
  } = useWizardRequiredGate(userType, skipWizardGate);

  const sessionEnded = isAuthenticated && isSessionTimedOut();

  useLayoutEffect(() => {
    if (sessionEnded) {
      dispatch(logout());
    }
  }, [dispatch, sessionEnded]);

  const handleGoToLogin = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  // Step 0: Show token validation loading
  if (isTokenValidating) {
    return <AuthLoadingState message="Verifying authentication..." />;
  }

  // Step 1: End an idle session, then the unauthenticated check sends them to login
  if (sessionEnded) {
    return null;
  }

  // Step 2: Handle token validation errors (fail-secure)
  if (tokenError && failSecure) {
    return (
      <AuthErrorState
        message={tokenError}
        onRetry={revalidate}
        onLogin={handleGoToLogin}
      />
    );
  }

  // Step 3: Check token validity
  if (!tokenState.isValid || !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Step 4: Check if user type exists
  if (!userType) {
    return <AuthLoadingState message="Loading user information..." />;
  }

  // Step 5: Check user type match
  if (requiredUserType && userType !== requiredUserType) {
    return <Navigate to="/" replace />;
  }

  // Step 6: Check permissions
  if (requiredPermissions.length > 0) {
    const hasPermission = checkPermissions(permissions, requiredPermissions, permissionMode);
    if (!hasPermission) {
      return <PermissionDenied />;
    }
  }

  // Step 6b: Lock brand/influencer users on the wizard until required fields exist
  if (!skipWizardGate && isBrandOrInfluencer(userType)) {
    const confirmedReturn = renderConfirmedWizardReturn(location, confirmedComplete);
    if (confirmedReturn) return confirmedReturn;
    if (isWizardChecking) {
      return <AuthLoadingState message="Checking onboarding..." />;
    }
    if (wizardError) {
      return (
        <AuthErrorState
          message={wizardError}
          onRetry={retryWizardCheck}
          onLogin={handleGoToLogin}
        />
      );
    }
    if (!isWizardComplete) {
      const redirect = renderWizardOnboardingRedirect(userType, location);
      if (redirect) return redirect;
    }
  }

  // Step 7: Handle profile completion errors (fail-secure)
  if (profileError && failSecure && !skipProfileCheck) {
    return (
      <AuthErrorState
        message="Unable to verify profile status. Please try again."
        onRetry={revalidate}
        onLogin={handleGoToLogin}
      />
    );
  }

  // Step 8: Show loading state while checking profile
  if (isProfileLoading && !skipProfileCheck) {
    return <AuthLoadingState message="Checking profile..." />;
  }

  // Step 9: Check profile completion and show modal if needed
  const showModal = !skipProfileCheck && !isComplete && !isProfileLoading;

  return (
    <>
      {/* Show modal if profile is incomplete */}
      <ProfileCompletionModal
        open={showModal}
        userType={userType}
        missingFields={missingFields}
        completionPercentage={completionPercentage}
      />

      {/* Only render children if profile is complete or check is skipped */}
      {!showModal && children}
    </>
  );
}

export function WizardOnboardingLock({ isAuthenticated, userType, children }) {
  const location = useLocation();
  const allowedPath = isAllowedWithoutWizard(location.pathname);
  const skip = !isAuthenticated || userType === 'admin' || allowedPath;
  const waitForUserType = isAuthenticated && !userType && !allowedPath;
  const {
    isChecking,
    isComplete,
    error,
    retry,
    confirmedComplete,
  } = useWizardRequiredGate(userType, skip || waitForUserType);

  const confirmedReturn = renderConfirmedWizardReturn(location, confirmedComplete);
  if (confirmedReturn) return confirmedReturn;

  if (waitForUserType || (!skip && isChecking)) {
    return <AuthLoadingState message="Checking onboarding..." />;
  }

  if (!skip && error) {
    return (
      <AuthErrorState
        message={error}
        onRetry={retry}
      />
    );
  }

  if (!skip && !isComplete) {
    const redirect = renderWizardOnboardingRedirect(userType, location);
    if (redirect) return redirect;
  }

  return children;
}

// Export helper components for testing
export {
  AuthLoadingState,
  AuthErrorState,
  PermissionDenied,
  useTokenValidation,
  checkPermissions,
};

export default ProtectedRoute;
